<!-- eslint-disable vue/no-v-html -->
<!-- eslint-disable vue/no-v-model-argument -->
<template>
  <div class="RequestList m-3">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div class="d-flex align-items-center">
        <div class="status-filters d-flex gap-1">
          <div
            v-for="(filter, key) in statusFilters.filters"
            :key="key"
            class="d-flex align-items-center"
          >
            <span
              v-if="filter.stepGroup"
              class="mr-2 ml-3"
            />
            <Button
              type="button"
              :class="selectedStatusFilter === key ? 'p-button-primary p-button-sm' : statusFilters.class"
              :label="$t(`processes.statusFilters.${key}`)"
              :disabled="isLoading"
              :badge="String(filter.count)"
              :badge-class="`p-badge-${selectedStatusFilter === key ? 'primary' : filter.badge || 'secondary'}`"
              @click="handleFilterByStatus({ filter: key })"
            />
          </div>
        </div>
      </div>
      <span class="mr-3 d-flex ml-auto align-items-center">
        <span
          v-if="isExporting"
          class="mr-3"
        >{{ processesForExport.length }} / {{ totalRecords }}</span>
        <Button
          v-tooltip.left="{ value: $t('Processes.refresh') }"
          type="button"
          :class="['p-button px-3 p-button-secondary p-button-outlined']"
          @click="handleRefresh"
        >
          <i class="fa fa-refresh" />
        </Button>
      </span>
      <h4
        v-if="processTypeTitle"
        class="m-0"
      >
        {{ isPrivateProcess ? $t('navbar.processes') : $t(`navbar.process_types.${selectedProcessType}`) }}
      </h4>
    </div>
    <div class="card assets">
      <DataTable
        v-if="filtersLoaded"
        id="locationTable"
        ref="table"
        :key="tableKey"
        :value="processes"
        :paginator="true"
        class="p-datatable-sm editable-cells-table asset-table"
        show-grid-lines
        :rows="perPage"
        :first="first"
        :total-records="totalRecords"
        paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        :rows-per-page-options="[10, 20, 50]"
        current-page-report-template="{first} tot {last} van {totalRecords}"
        :striped-rows="!isLoading"
        data-key="uuid"
        :filters.sync="filters"
        :lazy="true"
        filter-display="menu"
        removable-sort
        :sort-field="sortField"
        :sort-order="sortOrder"
        :sort-mode="sortMode"
        show-gridlines
        :scrollable="true"
        scroll-height="calc(100vh - 19rem)"
        responsive-layout="scroll"
        :reorderable-columns="!isDragDisabled"
        :resizable-columns="true"
        :auto-layout="true"
        :selection.sync="selectedRows"
        :meta-key-selection="true"
        selection-mode="multiple"
        :table-style="{ 'min-width': `${tableWidth}px`, 'width': `${tableWidth}px` }"
        :row-class="rowClass"
        :global-filter-fields="globalSearchFields"
        @page="handlePagination"
        @column-reorder="handleColumnReorder"
        @column-resize-end="handleColumnResize"
        @value-change="$event => filteredRecords = $event"
        @row-click="handleRowClick"
        @sort="handleColumnSort"
      >
        <template #header>
          <div class="d-flex py-2 gap-1">
            <Button
              v-tooltip.right="{ value: $t('Processes.clearAllFilters') }"
              type="button"
              icon="pi pi-filter-slash"
              :class="['p-button', { 'p-disabled p-button-secondary p-button-outlined': !isFiltered }]"
              @click="clearAllFilters()"
            />
            <span class="p-input-icon-left p-input-icon-right mr-auto w-25">
              <i class="pi pi-search no-events" />
              <InputText
                :value="searchTerm"
                :placeholder="$t('Processes.search')"
                class="w-100"
                @input="handleInput"
              />
              <i
                v-if="searchTerm"
                class="pi pi-times pointer"
                @click="clearGlobalSearch"
              />
            </span>
            <!-- <span>
              <Button
                v-if="canDeleteListedProcesses"
                v-tooltip.top="{ value: selectedRows.length ? $t('Processes.deselectAll'): $t('Processes.selectAll') }"
                type="button"
                icon="pi pi-check-square"
                :class="['p-button px-3', { 'p-button-secondary p-button-outlined': !selectedRows.length }]"
                @click="handleSelectedRecords"
              />
            </span> -->
            <span>
              <Button
                id="resetTable"
                v-tooltip.top="{ value: $t('requests.tableHeader.restoreTable') }"
                type="button"
                icon="pi pi-replay"
                :class="['p-button px-3', { 'p-disabled p-button-secondary p-button-outlined': !isTableChanged }]"
                @click="resetTableSettings()"
              />
            </span>
            <span style="position: relative">
              <MultiSelect
                :value="activeColumns"
                :options="defaultColumnFields"
                scroll-height="calc(80vh - 7em)"
                option-label="label"
                class="selectable-fields w-100"
                @change="handleActiveColumns"
              >
                <template #value>
                  {{ $t('Processes.selectColumns') }}
                </template>
              </MultiSelect>
            </span>
            <span>
              <div
                v-if="isExporting"
                class="p-button p-button-success"
                style="width:104px;"
              >
                <ProgressSpinner
                  style="width:22px;height:22px"
                  stroke-width="3"
                  animation-duration="1s"
                />
              </div>
              <Button
                v-else
                type="button"
                icon="pi pi-external-link"
                label="Export"
                class="p-button p-button-success"
                @click="exportCsv()"
              />
            </span>
          </div>
        </template>
        <template #empty>
          {{ $t('noRecordsFound', { type: $t('navbar.processes').toLowerCase() }) }}
        </template>
        <Column
          v-for="(column, index) in activeColumns"
          :id="column.field"
          :key="`${column.field}-${index}`"
          :field="column.field"
          :sort-field="`${column.field}`"
          :filter-field="column.field"
          :max-constraints="1"
          :filter-match-mode-options="filterOptions[column.field] ? matchModes : null"
          :show-filter-match-modes="false"
          :show-filter-operator="false"
          :show-add-button="!filterOptions[column.field]"
          :show-apply-button="!filterOptions[column.field] && column.fn !== 'setDate'"
          :show-clear-button="!filterOptions[column.field]"
          :exclude-global-filter="!filterOptions[column.field]"
          :hidden="column.hidden || column.exportOnly"
          :sortable="sortableColumns(column.field)"
          :frozen="index === 0 && frozenColumns.includes(column.field)"
          align-frozen="left"
          :header-class="column.field"
          :body-style="'padding: 0.3vh 0.5rem;'"
          :styles="{ 'flex': `0 0 ${column.width}px` }"
        >
          <template #header>
            {{ setColumnLabel(column.field) }}
            <span
              v-if="index === 0"
              :class="`ml-auto pi pi-lock${!frozenColumns.includes(column.field) ? '-open' : ''} freeze`"
              @click="handleFrozenColumns({ column: column.field })"
            />
          </template>
          <template
            v-if="!column.filterDisabled "
            #filter="{ filterModel, filterCallback }"
          >
            <div
              v-if="filterOptions[column.field]"
              style="max-height: calc(50vh - 7em); overflow: auto; padding:0 0.5em; max-width: 300px;"
            >
              <strong class="d-block pb-2">Selecteer {{ column.label.toLowerCase() }}</strong>
              <span
                v-if="filterOptions[column.field].length > 15 || filterSearchField"
                class="p-input-icon-right mb-2 filter-search"
              >
                <div class="p-input-icon-right">
                  <i class="pi pi-search" />
                  <InputText
                    v-model="filterSearchField"
                    type="text"
                    placeholder="Zoek"
                    @input="$event => handleFilterItemsSearch($event, column)"
                  />
                </div>
                <div
                  v-if="filterModel.value"
                  class="filter-chips d-flex py-2"
                >
                  <Chip
                    v-for="chip in filterModel.value"
                    :key="chip"
                    :label="setSelectedFilterLabel(column, chip)"
                    removable
                    class="chip-small"
                    @remove="handleFilterCallback(filterCallback, chip, filterModel)"
                  />
                </div>
              </span>
              <div
                v-for="filter in filterOptions[column.field]"
                :key="filter.value"
                class="d-flex align-items-center my-1"
              >
                <Checkbox
                  :id="filter.value"
                  v-model="filterModel.value"
                  :input-id="filter.value"
                  :name="filter.value"
                  :value="filter.value"
                  @change="filterCallback()"
                />
                <label
                  :for="filter.value"
                  role="button"
                  class="mx-2 mb-0 flex-fill"
                >{{ setFilterValue(filter) }}</label>
                <Badge
                  v-if="filter.count"
                  :value="filter.count"
                  class="facet-counter"
                  severity="info"
                />
              </div>
            </div>
            <DatePicker
              v-else-if="column.fn === 'setDate'"
              v-model="filterModel.value"
              locale="nl"
              :can-reset="false"
              :date-format-options="datePickerFormat"
              @input="filterCallback()"
            />
            <InputText
              v-else
              v-model="filterModel.value"
              type="text"
              class="p-column-filter"
              placeholder="Zoek"
              @input="event => handleInput(event, column.field, filterCallback)"
            />
          </template>
          <template #body="{ data }">
            <div
              v-if="!isLoading"
              :class="column.class"
            >
              <span
                v-if="isLink(column, data[column.field])"
                class="d-flex align-items-center"
              >
                <i class="pi pi-link mr-2" />
                <router-link
                  :to="getRouteName(data)"
                  v-html="setColumnValue({ column, fieldValue: data[column.field], data })"
                />
              </span>
              <span v-else>
                <img
                  v-if="column.icon && data[column.field]"
                  :src="getImage(data[column.field])"
                  width="15"
                  class="mr-2"
                >
                <span v-html="setColumnValue({ column, fieldValue: data[column.field], data })" />
              </span>
              <!-- <span v-else v-html="setColumnValue({ column, fieldValue: data[column.field], data })" /> -->
            </div>
            <Skeleton v-else />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import MultiSelect from 'primevue/multiselect'
import Tooltip from 'primevue/tooltip'
import Skeleton from 'primevue/skeleton'
import Checkbox from 'primevue/checkbox'
import Chip from 'primevue/chip';
import Badge from 'primevue/badge';
import ProgressSpinner from 'primevue/progressspinner';
import { FilterMatchMode, FilterOperator, FilterService } from 'primevue/api/'
import { image } from '@/helpers/assets'
import DatePicker from '@/components/form/elements/DatePicker'

import recordList from '@/helpers/recordList'

// eslint-disable-next-line no-unused-vars
import { exportCsv } from '@/config'
import { downloadExcel } from '@/services/exportCsv'

export default {
  name: 'Processes',
  components: {
    DataTable,
    Column,
    InputText,
    Button,
    MultiSelect,
    Skeleton,
    Checkbox,
    Chip,
    Badge,
    ProgressSpinner,
    DatePicker
  },
  directives: {
    'tooltip': Tooltip,
  },
  data() {
    return {
      searchTerm: null,
      filters: {
        global: { value: '', matchMode: FilterMatchMode.CONTAINS }
      },
      year: '',
      matchModes: [
        { label: 'filterByArrayEquals', value: 'filterByArrayEquals' },
        { label: 'commaSeparatedArrayOR', value: 'commaSeparatedArrayOR', method: 'some' },
        { label: 'commaSeparatedArrayAND', value: 'commaSeparatedArrayAND', method: 'every' },
      ],
      step4Uuid: 'b3fc2e59-25ee-4792-a1b3-27e69b277ee0',
      processes: [],
      filteredRecords: [],
      page: 1,
      perPage: 10,
      image,
      debounceTimeout: null,
      defaultColumns: [],
      activeColumns: [],
      processesStorage: {
        realisation: {},
        expansion: {},
        removal: {},
        replacement: {},
        reposition: {},
        private: {}
      },
      tableWidth: 0,
      tableKey: 0,
      frozenColumns: ['locationId'],
      selectedRows: [],
      expandedRows: [],
      sortMode: 'single', //or multiple
      sortField: '',
      sortOrder: 1,
      showDetails: false,
      datePickerFormat: { year: 'numeric', month: '2-digit', day: '2-digit' },
      selectedStatusFilter: 'all',
      activeAssetProcesses: null,
      showManualLocation: false,
      showImportLocations: false,
      isExporting: false,
      exportPagesLength: 1,
      exportPage: 1,
      exportTotal: 0,
      userMunicipalities: null,
      stakeholderUser: null,
      caseViewerCustomers: null,
      selectedProcessType: {},
      actionsModel: [
        {
					label: 'Import locaties',
					icon: 'pi pi-upload',
					command: () => {
						this.showImportLocations = true
					}
				}
      ],
      enabledContextMenu: [
        {
          label: 'Maak nieuw process aan',
          class: 'context-menu-title',
          disabled: true
        }
      ],
      filterOptions: {},
      filterSearchField: '',
      disabledContextMenu: [
        { label: 'Om een nieuw proces te starten, moeten alle locatieprocessen afgerond zijn!', icon: 'pi pi-fw pi-ban' }
      ],
      showComments: false,
      selectedLocation: null,
      selectedAsset: null,
      isLoading: true,
      filtersLoaded: false,
      totalRecords: 0,
      assets: [],
      defaultColumnFields: [],
      exportOnlyFields: [],
      isDragDisabled: false,
      hasRecordsChanged: false,
      processesForExport: [],
      tenant: process.env.VUE_APP_TENANT,
      storageVersion: 0.1, // Force storage update when needed
    }
  },
  computed: {
    ...mapGetters('user', ['canAccessAssetManagement', 'getCurrentUser', 'userRole']),
    ...mapGetters('tenant', ['getContractorByUuid', 'getMunicipalityOptions', 'getDefaultProcessTableFields', 'getActiveProcessTypes', 'getBaseUrl', 'getSteps', 'getProcessFieldLabels']),
    ...mapGetters('user', ['canAccessRealisationProcesses', 'canAccessExport', 'canDeleteListedProcesses', 'userRole']),
    ...mapGetters('processes', ['getProcessTableRecords', 'getCurrentProcessType', 'getCounts']),

    globalSearchFields () {
      return this.activeColumns?.filter(c => c.fn !== 'setFilterOptions').map(column => column.field)
    },
    isPrivateProcess() {
      return this.getCurrentProcessType === 'private'
    },
    isFormGeneratedProcess () {
      return ['brussels', 'wallonia', 'takeover'].includes(this.getCurrentProcessType)
    },
    hasRoleBasedFacets () {
      return !['private', 'brussels', 'wallonia', 'takeover'].includes(this.getCurrentProcessType)
    },
    isSteplessProcess () {
      return this.isPrivateProcess || this.isFormGeneratedProcess
    },
    first() {
      return (this.page - 1) * this.perPage
    },
    isFiltered () {
      return this.searchTerm?.length ||
             Object.keys(this.filters).some(f => this.filters[f].value?.length || this.filters[f]?.constraints?.some(c => c.value?.length))
    },
    activeFilters () {
      const activeFilters = {}
      Object.keys(this.filters)
        .map(filter => {
          const constraint = this.filters[filter]?.constraints?.find(c => c.value?.length)
          if (this.filters[filter].value || constraint) {
            activeFilters[filter] = this.filters[filter].value || constraint
          }
        })
      return activeFilters
    },
    isArrayDefaultFields () {
      return this.getDefaultProcessTableFields && Array.isArray(this.getDefaultProcessTableFields)
    },
    defaultFieldsNoProcessType () {
      return this.isArrayDefaultFields ? this.getDefaultProcessTableFields : []
    },
    statusFilters () {
      return {
        filters: {
          all: { count: 0 },
          active: { count: 0 },
          onhold: { count: 0 },
          completed: { count: 0 },
          cancelled: { count: 0 },
          ...( this.hasRoleBasedFacets
            ? {
                municipality: { count: 0, badge: 'success' },
                cpo: { count: 0, badge: 'success' },
              }
            : {}
          )
        },
        class: 'p-button-sm p-button-outlined p-button-secondary',
      }
    },
    isTableChanged () {
      const fields = this.getDefaultProcessTableFields?.[this.getCurrentProcessType] ||
                     this.getDefaultProcessTableFields.realisation ||
                     this.defaultFieldsNoProcessType

      const defaultColumns = fields.filter(field => !field.fields && !field.hidden && !field.exportOnly)
      const activeColumns = this.activeColumns?.filter(column => !column.hidden && !column.exportOnly)

      const tableChanged = activeColumns.length && defaultColumns.some((column, index) => {
        const activeColumn = activeColumns[index]
        return activeColumn && ((column.field !== activeColumn.field) || (column.width !== activeColumn.width))
      })

      return activeColumns.length &&
        (tableChanged || (this.processesStorage && this.processesStorage.version !== this.storageVersion))
    },
    hasActiveRows () {
      return [this.selectedRows, this.expandedRows].some(rows => rows.length === 1)
    },
    isDevelopment () {
      return ['-dev', '-demo'].some(type => this.getBaseUrl?.includes(type))
    },
    getSortOrder () {
      return this.sortOrder ? this.sortOrder === 1 ? 'asc' : 'desc' : null
    },
    activeSearchFilters () {
      const filters = {}
      Object.keys(this.activeFilters).map(filter => filters[filter] = this.activeFilters[filter].value.toString())
      return filters
    },
    activeProcessType () {
      return this.getActiveProcessTypes.find(type => type.value === this.getCurrentProcessType)
    },
    processTypeTitle () {
      return this.activeProcessType?.label
    },
    isRealisationProcess() {
      return this.getCurrentProcessType === 'realisation'
    },
    queryParams () {
      const queryParams = {
        processType: this.getCurrentProcessType || this.selectedProcessType?.value,
        ...( this.selectedStatusFilter !== 'all' ? { status: this.selectedStatusFilter } : {} ),
        ...( this.searchTerm && typeof this.searchTerm === 'string' ? { searchTerm: this.searchTerm } : {} ),
        ...( this.isFiltered ? { ...this.activeSearchFilters } : {}),
        ...( this.sortField ? { order: this.sortField, orderType: this.getSortOrder } : {}),
        ...( !this.isExporting
          ? {
              page: this.page,
              pageSize: this.perPage
            }
          : {}
        )
      }
      return queryParams
    },
    queryString () {
      return  new URLSearchParams([...Object.entries(this.queryParams)]).toString()
    },
    identity () {
      return this.$auth.user['https://evtools.nl/identity']
    }
  },
  watch: {
    activeColumns: {
      deep: true,
      handler(activeColumns) {
        if (activeColumns && this.processesStorage) {
          this.processesStorage[this.getCurrentProcessType].activeColumns = activeColumns
          localStorage.setItem('processes', JSON.stringify(this.processesStorage))
        }
      },
    },
    filters: {
      deep: true,
      async handler(filters) {
        if (this.filters) {
          Object.keys(this.filters).map(filter => {
            if (this.filters[filter].constraints && !this.filters[filter].operator) {

              this.filters[filter].operator = FilterOperator.OR
              this.filters[filter].constraints[0].matchMode = FilterMatchMode.CONTAINS
            }
          })
        }

        this.page = 1
        this.selectedRows = this.expandedRows = []

        this.processesStorage[this.getCurrentProcessType].filters = filters
        localStorage.setItem('processes', JSON.stringify(this.processesStorage))
      },
    },
    queryParams: {
      handler (query, prevQuery) {
        const hasRouteChanged =
          prevQuery.page && (prevQuery.processType && prevQuery.processType === query.processType)

        if (this.isExporting || !hasRouteChanged) return

        // Only after the query is set //
        this.fetchProcesses()
      }
    },
    defaultColumnFields: {
      immediate: true,
      handler () {
        this.setColumnLabels()
      }
    },
    '$i18n.locale': {
      handler () {
        this.setColumnLabels()
        this.setDropdownColumnFields()
      }
    },
    '$route.params.type': {
      immediate: true,
      async handler(route) {
        this.selectedProcessType = this.getActiveProcessTypes.find(process => process.routeType === route)?.value || this.getCurrentProcessType

        if (!this.selectedProcessType) return

        if (this.selectedProcessType) {
          await this.$store.dispatch('processes/setCurrentProcessType', { processType: this.selectedProcessType })
        }
        if (!this.getCurrentProcessType || !this.getDefaultProcessTableFields) return

        if (this.getCurrentUser?.caseviewer?.length) {
          this.caseViewerCustomers = this.getCurrentUser.caseviewer.map(customer => customer.value)
        } else if (!this.userMunicipalities && (this.getCurrentUser && Object.keys(this.getCurrentUser).length)) {
          // If user has a restricted municipality access (no wildcard) //
          const [municipalities] = Object.values(this.getCurrentUser)
          const wildcard = municipalities.some(mun => typeof mun === 'string')
          this.userMunicipalities = !wildcard ? municipalities.map(mun => mun.value) : null
        }

        if (this.getCurrentUser && !this.stakeholderCategory('admin')) {
          this.stakeholderUser = this.stakeholderCategory(this.userRole)
        }

        if (this.isExporting) return

        this.setDefaultProcessTableFields()
        this.fetchProcesses(route)
      }
    }
  },
  created() {
    //this.registerDropdownFilters()
  },
  beforeDestroy() {
    this.removeActiveMenuFilter()
    clearTimeout(this.debounceTimeout)
  },
  methods: {
    stakeholderCategory (category) {
      return this.identity?.category === category && this.identity.uuid
    },
    setFilterValue (filter) {
      const label = filter.label || filter.text
      return label[this.$i18n.locale] || label
    },
    setDefaultProcessTableFields () {
      this.exportOnlyFields = []
      this.filtersLoaded = false

      const defaultTableFields = this.getDefaultProcessTableFields[this.getCurrentProcessType] ||
                                 this.getDefaultProcessTableFields.realisation ||
                                 this.defaultFieldsNoProcessType

      const columns = defaultTableFields.filter(column => {
        if (column.exportOnly) {
          // Pushing fields that are used for export only (not used in the table) //
          this.exportOnlyFields.push(column)
        } else {
          return true
        }
      })

      this.defaultColumns = JSON.parse(JSON.stringify(columns))
      this.defaultColumnFields = this.defaultColumns.filter(column => !column.hidden)

      const processesStorage = localStorage.getItem('processes')
      if (processesStorage) {
        this.processesStorage = JSON.parse(localStorage.getItem('processes'))
      }

      const storageByType = this.processesStorage?.[this.getCurrentProcessType]

      const hasProcessTypeStorage = storageByType && !!Object.keys(storageByType).length
      const version = this.processesStorage?.version || 0
      const storageTenant = this.processesStorage?.tenant
      if (this.storageVersion !== version || this.tenant !== storageTenant) {
        Object.keys(this.processesStorage)
          .filter(key => !['tenant', 'version'].includes(key))
          .forEach(key => this.processesStorage[key] = {})
      }

      if (!processesStorage || !hasProcessTypeStorage || this.storageVersion !== version || this.tenant !== storageTenant) {
        this.setDefaultProcessFields()
        this.setTableFilters()
      } else {
        this.tableKey++ // Sometikmes needed when switching between process types //
        const activeColumns = this.processesStorage[this.getCurrentProcessType].activeColumns

        this.activeColumns = activeColumns?.length ? activeColumns : this.defaultColumnFields
        this.filters = this.processesStorage[this.getCurrentProcessType].filters
        this.perPage = this.processesStorage[this.getCurrentProcessType].perPage || 10
        this.searchTerm = this.processesStorage[this.getCurrentProcessType].searchTerm || ''
        this.selectedStatusFilter = this.processesStorage[this.getCurrentProcessType].selectedStatusFilter || 'all'
      }

      this.page = Number(this.$route.query?.page) || 1
      this.setTableWidth()
      // Skeleton
      this.processes = Array(this.perPage || 10).fill(this.activeColumns[0])

      this.activeColumns.map(column => {
        if (column.dropdown && recordList[column.optionsFn]) {
          this.$set(this.filterOptions, column.field, recordList[column.optionsFn](true, column) ) // Set flag for options key-value pair //
        }
      })

      this.setDropdownColumnFields()
      this.setFrozenColumns()

      this.filtersLoaded = true
    },
    setColumnLabel (field) {
      const label = this.getProcessFieldLabels?.[field]?.[this.$i18n.locale]
      return label || this.$i18n.t(`recordList.${field}`)
    },
    setColumnLabels () {
      // Update column labels in dropdown (translations) //
      if (this.defaultColumnFields.length) {
        this.defaultColumnFields.map(field => {
          field.label = this.getProcessFieldLabels?.[field.field]?.[this.$i18n.locale] || this.$i18n.t(`recordList.${field.field}`)
          const index = this.activeColumns.findIndex(col => col.field === field.field)
          if (index !== -1) {
            this.activeColumns[index].label = field.label
          }
          return field
        })
      }
    },
    setRecords ({ records }) {
      const dropdownFilterSets = {}
      Object.keys(this.filters).map(filter => {
        if (this.filters[filter].constraints?.some(c => c.matchMode === 'filterByArrayEquals')) {
          dropdownFilterSets[filter] = new Set()
        }
      })
      this.processes = records
      this.isLoading = false

      this.processesStorage[this.getCurrentProcessType].searchTerm = this.searchTerm
      this.processesStorage[this.getCurrentProcessType].selectedStatusFilter = this.selectedStatusFilter
      localStorage.setItem('processes', JSON.stringify(this.processesStorage))
    },
    handleRefresh() {
      this.fetchProcesses()
    },
    isLink (column, value) {
      return value && (column.fn === 'setLink' || column.link)
    },
    sortableColumns (columnField) {
      return !['tagCount', 'vkbStatus', 'currentStatusMessage', 'currentStatus', 'stepProgressDays', 'MunicipalityCode', 'latestComment', 'serialNumbers', 'qrCode', 'evse', 'gridoperator', 'region'].includes(columnField)
    },

    getRouteName (data) {
      const lastStep =  this.getSteps?.at(-1)?.step
      const step = data.currentStep === 100 ? lastStep : data.currentStep
      const processType = data.processType.value
      const uuid = data.uuid
      // Branch on this row's own processType, not the currently active tab/filter
      // (this.isPrivateProcess/this.isFormGeneratedProcess) — those can disagree with the
      // row when viewing a mixed/"all" list, sending the row to a route name that was
      // never registered for its actual process type.
      const isPrivate = processType === 'private'
      const isFormGenerated = ['brussels', 'wallonia', 'takeover'].includes(processType)
      const name = isPrivate
        ? 'Process'
        : isFormGenerated
          ? `${processType}Steps`
          : `${processType}Step${step}`

      return {
        name,
        params: {
          uuid,
          ...( !isPrivate ? { type: data.processType.routeType } : {}),
          ...( !(isPrivate || isFormGenerated) ? { step } : {})
        }
      }
    },

    setSelectedFilterLabel (column, filter) {
      const field = this.filterOptions[column.field].find(field => field.value === filter)
      return field?.label?.[this.$i18n.locale] || field?.label
    },
    getProcessTypeTranslation(processType) {
      if (processType.hidden) return processType // manual, import ... //
      return this.getActiveProcessTypes.find(type => type.value === processType).routeType
    },
    getImage(image) {
      const name = `chargingpoint-${image.trim()}`
      return this.image({ name })
    },
    async clearGlobalSearch () {
      this.searchTerm = this.processesStorage[this.getCurrentProcessType].searchTerm = null
      localStorage.setItem('processes', JSON.stringify(this.processesStorage))

      this.page = 1
        this.$nextTick(() => {
          this.tableKey++
        })
    },
    handleRowClick (event) {
      // Deselct selected row only if single row is selected (meta-key-selection) //
      const isRowSelected = this.selectedRows.some(row => row.uuid === event.data.uuid)
      this.$nextTick(() => {
        if (isRowSelected && this.selectedRows.length === 1) {
          this.selectedRows = []
        }
      })
    },
    // Chips (tags) in filter drop-downs //
    handleFilterCallback (filterCallback, filterValue, filterModel) {
      const valueIndex = filterModel.value.findIndex(value => value === filterValue)
      filterModel.value.splice(valueIndex, 1)

      filterCallback()
    },

    setStatusFiltersCounts(statusFacet) {
      Object.keys(statusFacet)
        .forEach(status => {
          if (this.statusFilters.filters[status]) {
            this.statusFilters.filters[status].count = statusFacet[status]
          }
        })
    },

    // setFilterFacets (facets) {
    //   Object.keys(facets).map(facetField => {
    //     if (facetField === 'status') {
    //       return this.setStatusFiltersCounts(facets[facetField])
    //     }
    //     facetField === 'municipality' && console.log('gore', this.filterOptions[facetField])
    //     if (this.filterOptions[facetField]) {
    //       this.filterOptions[facetField].map(field => {
    //         //if (facets[facetField][field.value]) {
    //           field.count = facets[facetField]?.[field.value] || 0
    //         //}

    //       })
    //       facetField === 'municipality' && console.log('dole', facetField, facets[facetField])
    //     }
    //   })
    // },

    handleFilterItemsSearch (event, column) {
      this.filterOptions[column.field] = event?.length >= 3
      // recordList[column.field] func. => keyValuePair is true //
        ? recordList[column.optionsFn](true, column).filter(option => option.label.toLowerCase().includes(event.toLowerCase()))
        : recordList[column.optionsFn](true, column)
    },

    handleFilterByStatus({ filter }) {
      if (this.selectedStatusFilter === filter) return

      this.selectedStatusFilter = filter

      this.page = 1
      this.processesStorage[this.getCurrentProcessType].page = this.page
    },

    handleCloseSidebar (event) {
       // Wait for closing animation to finish //
      setTimeout(() => {
        this.showManualLocation = this.showImportLocations = false
        this.selectedAsset = null
        // If exact search on deleted locationId was active, reset filter //
        if (event?.resetFilter) {
          //this.clearGlobalSearch()
          this.selectedRows = []
        }
        if (event?.update && event?.asset) {
          this.activeAssetProcesses = event.asset?.processes
          this.tableKey++
          this.$nextTick(() => {
            this.selectedRows = this.expandedRows = [event.asset]
          })
        }
      }, 500)
    },

    async handleColumnSort(event) {
      if (this.sortMode === 'multiple') {
        // With sortMode multiple, removable sort cannot be properly done with this version
        // multiSortMeta updates only current column and if removable sort is selected, it gives back 'undefined'
        // witch makes it impossible to remove only that column. It resets sorting on all columns!
        const [sortField] = event.multiSortMeta
        const sortFieldIndex = this.multiSortMeta.findIndex(f => f.field === sortField?.field)

        if (sortFieldIndex !== -1) {
          this.multiSortMeta.splice(sortFieldIndex, 1)
        }

        if (sortField) {
          this.multiSortMeta.push(sortField)
        } else {
          this.multiSortMeta = []
        }

        this.processesStorage[this.getCurrentProcessType].multiSortMeta = this.multiSortMeta
      } else {
        this.processesStorage[this.getCurrentProcessType].sortField = this.sortField = event.sortField
        this.processesStorage[this.getCurrentProcessType].sortOrder = this.sortOrder = event.sortOrder
      }

      localStorage.setItem('processes', JSON.stringify(this.processesStorage))
    },


    handleColumnClick({ column, process }) {
      recordList[column.fn] && recordList[column.fn]({ process })
    },

    registerDropdownFilters () {
      this.matchModes.forEach(mode => {
        FilterService.register(mode.value, (data, value) => {
          if (!value?.length) {
            return true
          }
          // Dropdown with comma-separated values matching (e.g. Tags)
          const terms = data?.split(', ')
          const OR = mode.value.endsWith(FilterOperator.OR)
          const matchedValue = mode.method
            ? (value[mode.method](val => terms?.includes(val)) || OR && value.includes(data))
            : value.includes(data) // The usual dropdown (e.g Step => always OR operator)

          return matchedValue
        })
      })
    },

    // eslint-disable-next-line no-unused-vars
    async fetchProcesses (route) {
      const caseViewerCustomers = this.caseViewerCustomers ? `&caseViewerCustomers=${this.caseViewerCustomers.join(',')}` : ''
      const userMunicipalities = this.userMunicipalities ? `&userMunicipalities=${this.userMunicipalities.join(',')}` : ''
      let stakeholderUser = this.stakeholderUser ? `&stakeholderUser=${this.stakeholderUser}` : ''
      // Temp. for non-stapless processes, no harm on stapless processes search //
      if (this.userRole === 'cpo') {
        stakeholderUser += `&userCpo=${this.stakeholderUser}`
      }
      const limitedAccess = caseViewerCustomers || stakeholderUser

      this.isLoading = true

      const api = await fetch(`${process.env.VUE_APP_AWS_API_BASE_URL}/processes?${this.queryString}${limitedAccess}${userMunicipalities}`, {
        method: 'GET',
        headers: {
          'x-api-key': process.env.VUE_APP_AWS_API_KEY,
        },
      }).catch(err => console.log(err))

      if (!api?.ok) {
        throw new Error('server error')
      }

      const response = await api.json()

      if (response.data.meta) {
        const records = response.data.records
        this.totalRecords = response.data.meta.total

        this.setRecords({ records })
        if (response.data.meta.facets?.status) {
          this.setStatusFiltersCounts(response.data.meta.facets?.status)

        }
        // Until we find a solution to retain counts within dropdown filters click (OR) //
        //this.setFilterFacets(response.data.meta.facets)

        return response.data.records
      }
    },

    async fetchProcessByUuid (data, projection) {
      if (!data?.uuid) return

      const queryString = projection
        ? `?${new URLSearchParams([...Object.entries(projection)]).toString()}`
        : ''

      const api = await fetch(`${process.env.VUE_APP_AWS_API_BASE_URL}/processes/${data.uuid}${queryString}`, {
        method: 'GET',
        headers: {
          'x-api-key': process.env.VUE_APP_AWS_API_KEY,
        },
      })
      if (! api.ok) {
        throw new Error('server error')
      }

      return await api.json()
    },

    setColumnValue({ column, fieldValue, data }) {
      let value = recordList[column.fn]
        ? recordList[column.fn]({ column, fieldValue, record: data })
        : fieldValue ? fieldValue : '-'

      return (value || value === 0) ? this.highlightSearchTerm(value, column.field, column) : '-'
    },

    rowExpanderClass (record) {
      const hasCompletedProcess = record.allProcessesComplete
      const chevronType = this.isRowExpanded(record.locationId) ? 'pi pi-chevron-circle-down' : 'pi pi-chevron-circle-right'

      return chevronType + (hasCompletedProcess ? ' has-completed-process' : '')
    },

    rowClass (data) {
      return this.expandedRows.some(row => row.processLocationId === data.locationId) && 'expanded-row'
    },

    async fetchProcessesForExport () {
      this.isExporting = true
      const perPage = 400
      const caseViewerCustomers = this.caseViewerCustomers ? `&caseViewerCustomers=${this.caseViewerCustomers.join(',')}` : ''
      const userMunicipalities = this.userMunicipalities ? `&userMunicipalities=${this.userMunicipalities.join(',')}` : ''
      let stakeholderUser = this.stakeholderUser ? `&stakeholderUser=${this.stakeholderUser}` : ''
       // Temp. for non-stapless processes, no harm on stapless processes search //
      if (this.userRole === 'cpo') {
        stakeholderUser += `&userCpo=${this.stakeholderUser}`
      }
      const limitedAccess = caseViewerCustomers || stakeholderUser

      const query = `${this.queryString}${limitedAccess}${userMunicipalities}&isExport=true&page=${this.exportPage}&pageSize=${perPage}`
      const api = await fetch(`${process.env.VUE_APP_AWS_API_BASE_URL}/processes?${query}`, {
        method: 'GET',
        headers: {
          'x-api-key': process.env.VUE_APP_AWS_API_KEY,
        }
      })
      if (! api.ok) {
        throw new Error('server error')
      }

      const response = await api.json()

      this.processesForExport = [...this.processesForExport, ...response.data.records]
      // Use skip/limit insted of cursor (the cursor not always updated properly, while using faceting) //
      this.exportTotal = response.data.meta.total
      this.exportPagesLength = Math.round(this.exportTotal / perPage)

      if ((this.exportPagesLength > (this.exportPage -1))) { // -1 => first call has already been made //
        this.exportPage++
        return await this.fetchProcessesForExport()
      }

      return this.processesForExport
    },

    async exportCsv () {
      const processes = await this.fetchProcessesForExport({ after: null })

      this.isExporting = false
      this.processesForExport = []
      this.exportPagesLength = 0
      this.exportPage = 1

      exportCsv.worksheetColumns = []
      // Add columns (fields) meant only for export, otherwise not to be used in the table //
      const defaultFields = [...this.defaultColumnFields, ...this.exportOnlyFields].map(column => column.field)
      const exportColumns = [...this.activeColumns.filter(col => !col.notExport), ...this.exportOnlyFields]

      exportColumns
        .filter(column => defaultFields.includes(column.field))
        .map(column => {
          exportCsv.worksheetColumns.push({
            header: column.label?.[this.$i18n.locale] || column.label, // this.$i18n.locale is used for exportOnlyFields //
            key: column.field,
            link: column.link || column.fn === 'setLink',
            fn: column.fn,
            width: Math.ceil(column.width / 10), // approx. translation to Excel column width
          })
        })

      const records = JSON.parse(JSON.stringify(processes))

      exportCsv.file.name = 'processes'
      exportCsv.file.acronym = this.activeProcessType?.acronym || ''
      downloadExcel({ config: exportCsv, records })
      this.perPage = this.processesStorage[this.getCurrentProcessType].perPage
    },

    handleFrozenColumns({ column }) {
      const index = this.frozenColumns.findIndex(c => c === column)
      index !== -1
        ? this.frozenColumns.splice(index, 1)
        : this.frozenColumns.push(column)
    },

    setDefaultProcessFields() {
      const defaultTableFields = this.getDefaultProcessTableFields[this.getCurrentProcessType] ||
                                 this.getDefaultProcessTableFields.realisation ||
                                 this.defaultFieldsNoProcessType

      const columns = defaultTableFields.filter(column => {
        if (column.exportOnly) {
          // Pushing fields that are used for export only (not used in the table) //
          this.exportOnlyFields.push(column)
        } else {
          return true
        }
      })
      this.defaultColumns = JSON.parse(JSON.stringify(columns))

      this.defaultColumnFields = this.defaultColumns.filter(column => !column.hidden)
      this.activeColumns = this.defaultColumnFields
      // Reset storage values to default //

      this.processesStorage[this.getCurrentProcessType] = {
        activeColumns: this.activeColumns,
        filters: this.filters,
        perPage: this.perPage
      }
      this.processesStorage.version = this.storageVersion
      this.processesStorage.tenant = this.tenant

      localStorage.setItem('processes', JSON.stringify(this.processesStorage))
    },

    handleColumnResize(event) {
      const [column] = event.element.classList
      const fieldIndex = this.activeColumns.findIndex(f => f.field === column)
      const width = this.activeColumns[fieldIndex].width + event.delta

      event.element.style = `flex: 0 0 ${width}px`
      this.activeColumns[fieldIndex].width = width
    },

    handleColumnReorder(event) {
      const dropIndex = event.dropIndex ? event.dropIndex : 0
      const [movedField] = this.activeColumns.splice(event.dragIndex, 1)

      this.activeColumns.splice(dropIndex, 0, movedField)

      this.setFrozenColumns()
      this.setDropdownColumnFields()
      this.setTableWidth()
      this.tableKey++
    },

    handleActiveColumns(event) {
      const activeColumns = event.value.filter(col => !col.hidden && !col.exportOnly)
      activeColumns.sort((a, b) => {
        return this.defaultColumnFields.findIndex(f => f.field === a.field) -
          this.defaultColumnFields.findIndex(f => f.field === b.field)
      })
      this.activeColumns = activeColumns
      this.setFrozenColumns()

      this.setTableWidth()
    },

    setTableWidth() {
      this.$nextTick(() => {
        if (this.activeColumns) {
          const widths = this.activeColumns.filter(col => !col.hidden && !col.exportOnly).map(field => field.width)
          this.tableWidth = widths.reduce((total, width) => Number(total) + Number(width), 0)  // + 80 // The first two columns (expand./comments control)
        }
      })
    },

    async handleInput(event, field, filterCallback) {
      if (event && event.length < 3 ) return
      this.page = 1

      if (!event) {
        if (!field) { // Global search field //
          this.searchTerm = this.processesStorage[this.getCurrentProcessType].searchTerm = null
        }
      }
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(async () => {
         if (field) { // Column field search //
          filterCallback()
        } else {
          this.searchTerm = this.processesStorage[this.getCurrentProcessType].searchTerm = event
        }

        //localStorage.setItem('processes', JSON.stringify(this.processesStorage))
        //this.tableKey++
      }, 500)
    },

    setFrozenColumns () {
      const [firstColumn] = this.activeColumns
      this.frozenColumns = [firstColumn?.field]
    },

    setDropdownColumnFields () {
      this.defaultColumnFields.sort((a, b) => {
        return this.activeColumns.findIndex(f => f.field === a.field) - this.activeColumns.findIndex(f => f.field === b.field)
      })
    },
    // Highlight terms: convert value to label (filter dropdowns) //
    dropdownValueToLabel (term, column) {
      return this.filterOptions[column.field].find(field => (field.label?.[this.$i18n.locale] || field.label) === term)?.value
    },

    highlightSearchTerm (data, key, column) {
      const filterKey = key
      const hasFilterValue = this.filters[filterKey]?.constraints.some(c => c.value?.length)
      const isFilterDropdown = this.activeColumns.some(column => column.field === key && column.dropdown)
      const isDateValue = column.fn === 'setDate'

      let search = this.searchTerm //this.filters.global.value

      const match = (search && data && !isFilterDropdown) ? String(data).match(new RegExp(search, 'i')) : null

      if (match) {
          search = String(data).replace(match[0],
            `<span class="hl-search">${match[0]}</span>`,
          )
          if (!hasFilterValue) {
            return search
          }
      } else {
        search = ''
      }

			if (hasFilterValue) {
        this.filters[filterKey]?.constraints.map(constraint => {
          // date: convert YYYY-MM-DD to DD-MM-YYYY highlight only //
          const value = isDateValue ? constraint.value.trim().split('-').reverse().join('-') : constraint.value
          const terms = String(data).split(', ')
          const patternMatch = isFilterDropdown && constraint.value.includes(this.dropdownValueToLabel(data, column))
            ? [data]
            : String(data).match(new RegExp(value, 'i')) || []

          if (!patternMatch.length && isFilterDropdown && terms.length > 1) {
            // Dropdown with comma-separated values matching (e.g. Tags)
            terms.forEach(term => {
              if (value.includes(this.dropdownValueToLabel(term, column))) {
                patternMatch.push(term)
              }
            })

            patternMatch.forEach(p => {
              data = data.replace(p, `<span class="hl-filter">${p}</span>`)
            })

            search = data
          } else {
            search = String(search || data).replace(patternMatch[0],
              `<span class="hl-filter">${patternMatch[0]}</span>`,
            )
          }

        })
        return search || data
			}

      return data
		},

    setTableFilters () {
      this.activeColumns.map(column => {
        if (column.filterDisabled || column.hidden || column.exportOnly) return

        this.filters[column.field] = {
          operator: FilterOperator.OR,
          constraints: [{
            value: null,
            matchMode: column.matchMode || // commaSeparated values => different mode
              (column.fn === 'setFilterOptions' ? 'filterByArrayEquals' : FilterMatchMode.CONTAINS)
          }],
        }
      })
      // Render dataTable after all custom filters are set (to avoid customFilterService error) //
      this.$nextTick(() => {
        this.filtersLoaded = true
      })
    },

    async clearAllFilters() {
      Object.keys(this.filters).map(filter => {
        if (this.filters[filter].constraints) {
          this.filters[filter].constraints.map(constraint => constraint.value = null)
        }
      })
      this.clearGlobalSearch()
    },

    resetTableSettings() {
      this.setDefaultProcessFields()
      this.tableKey++
      this.setTableWidth()
      this.setFrozenColumns()
    },

    async handlePagination(event) {
      if ((this.page === 1 && event.page === 0) && this.perPage === event.rows) return

      this.page = this.perPage !== event.rows ? 1 : event.page + 1
      this.perPage = this.processesStorage[this.getCurrentProcessType].perPage = event.rows

      localStorage.setItem('processes', JSON.stringify(this.processesStorage))

      // this.$router.replace({
      //   name: 'RequestList',
      //   query:
      //   {
      //     ...(event.page > 0 ? { page: event.page + 1 } : {}),
      //     ...(event.rows > 10 ? { perPage: event.rows } : {}),
      //   },
      // })
    },

    removeActiveMenuFilter() {
      // PrimeVue dataTable column filter type menu overlay bug: on route change, it's not destroyed and stays visible //
      // Column template filter append filter menu to body instead of render within a header component //
      const filterMenu = document.querySelector('.p-column-filter-overlay')
      if (filterMenu) {
        filterMenu.remove()
      }
    },
  },
}
</script>

<style lang="scss">
.RequestList {
  .assets {
    border: none;

    .asset-table {
      width: 100% !important;
    }
  }

  .p-datatable.p-datatable-sm .p-datatable-tbody>tr>td {
    padding: 0.7vh 0.5rem;
  }
}

.gap-1 {
  gap: 1em;
}

.selectable-fields {
  min-width: 350px;

  .p-multiselect-item {
    &.p-highlight {
      background: none !important;
    }
  }
}

.p-component {
  font-family: Avenir, Helvetica, Arial, sans-serif !important;
}

.p-datatable.p-datatable-striped .p-datatable-tbody>tr:nth-child(even) {
  background: #f2f2f2 !important;
}

.p-paginator .p-dropdown {
  align-items: center;
}

.p-multiselect-header {
  &:after {
    content: 'Selecteer / deselecteer alles';
    position: absolute;
    left: 2.7em;
    pointer-events: none;
  }

  .p-checkbox {
    flex: 1;
  }
}

.p-skeleton {
  margin: 0.25em 0;
}

// SplitButton create-process
.create-process {
  .p-splitbutton-menubutton {
    position: absolute;
    width: 100%;
    background: none;
    justify-content: flex-end !important;
    padding-right: 1em;

    &:enabled:hover {
      background: none;

    }
  }

  .p-splitbutton-defaultbutton {
    padding-right: 2.5em;
  }
}

.process-link {
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.freeze {
  order: 1;
  color: #495057 !important;

  &.pi-lock {
    color: #2196F3 !important;
  }
}

.p-selectable-row {
  .p-badge {
    position: absolute;
    top: 3px;
    left: 6px;
    //border-radius: 0;
    width: 1.2em;
    height: auto;
    min-width: auto;
    font-size: 10px;
    line-height: 1.2;
    display: flex;
    justify-content: center;
    //background: #495057;
    &.badge-comments {
      right: 7px;
      left: auto;
      //background: #2196F3;
    }
  }
}

.p-selectable-row.p-highlight,
.p-datatable.p-datatable-striped .p-datatable-tbody>tr:nth-child(even).p-selectable-row.p-highlight,
.p-selectable-row.p-highlight a {
  background: #777 !important;
  color: white !important;
}

.p-selectable-row.p-highlight {
  .pi {
    color: white;
  }
}

.expanded-content {
  margin: -50% 1em 1em;
  opacity: 0;
  transition: margin-top 0.3s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.5s ease-out 0.1s;

  .expanded {
    box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
  }

  &.showDetails {
    opacity: 1;
    margin-top: 1em;
    padding: 0 1em 1em;
    width: 100%;

    .process-title {
      display: block;
      margin-bottom: 0.5em;
    }
  }
}

@keyframes p-progress-spinner-color {
    0% {
        stroke: #fff;
    }
    100% {
        stroke: #fff;
    }
}

// .has-completed-process {
//   position: relative;
//   &:before {
//     content: "";
//     position: absolute;
//     left: 0px;
//     top: 10%;
//     width: 3px;
//     height: 80%;
//     background: #8C2FAB;
//     z-index: 1;
//   }
// }

.p-datatable tr.expanded-row {
  >tr {
    >td {
      background: #f8f8f8f8;

      .p-row-toggler {
        background: #ddd;

        .p-row-toggler-icon {
          color: black;
          font-weight: 700;
        }
      }
    }
  }
}
.facet-counter {
  transform: scale(0.7) translateX(-8px);
  font-size: 1em !important;
  font-weight: normal !important;;
  background: none !important;
  border: 1px solid black;
  color: black !important;
}
.filter-search {
  position: sticky;
  top: 0;
  z-index: 1;
  .p-inputtext {
    &:focus {
      box-shadow: none;
      outline: none;
    }
  }
  .p-chip.p-component {
    &.chip-small {
      background-color: #777;
      color: white;
      border-radius: 4px;
      .p-chip-text {
        font-size: 11px;
      }
    }
  }
  .filter-chips {
    background: white;
    gap: 0.3em;
    flex-flow: row wrap;
  }
}
.fa.fa-refresh {
  font-size: 20px;
  color: #999;
}

.p-datatable-wrapper .p-datatable-table .p-datatable-tbody tr.p-datatable-row-expansion {
  border-left: 3px solid #777;
  //border-bottom: 3px solid #777;
  width: 100%; //2050px; // sum of expandedTableColumnWidths
  position: sticky;
  left: 0;

  td:nth-child(1) {
    flex: 0 0 100% !important;
    border: none;
  }
}

.p-contextmenu.p-component {
  width: auto;
  margin-top: -5em;
  padding-top: 0;
  .p-menuitem {
    &.context-menu-title {
      border-bottom: 1px solid #777;
      .p-disabled {
        opacity: 1;
        .p-menuitem-text {
          line-height: 0.6;
        }
      }
    }
  }
}
.create-process {
  .context-menu-title, .p-menu-separator {
    display: none;
  }
}

.p-column-header-content {
  flex: 1;
}

.p-frozen-column {
  .controls {
    gap: 0.5em;
    .pi, .has-comments {
    font-size: 1.2em;
    //color: #777;
  }
  }
  .has-comments, .has-completed-process {
    color: #2196F3;
  }
}
</style>
